import React, { FC, useState, useCallback } from 'react';
import Spinner from '@johnlewispartnership/wtr-ingredients/dist/ingredients/Spinner';
import Button from '@johnlewispartnership/wtr-ingredients/dist/ingredients/Button';
import { useAuthentication } from '@johnlewispartnership/wtr-website-authentication/dist/context';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import { useCommonModal } from 'components/CommonModal';
import { getInstantCheckoutErrorModal } from 'constants/modals/instantCheckoutErrorModals';
import { urls } from 'constants/urls';
import { useShoppingContext } from 'contexts/ShoppingContext';
import orderOrchestrationServiceClient from 'services/orderOrchestration';
import { pathJoin } from 'utils/general';

import styles from './InstantCheckoutModalContent.module.scss';

export type InstantCheckoutModalContentProps = {
  location: string;
  regularCheckoutDestination: string;
};

const InstantCheckoutModalContent: FC<InstantCheckoutModalContentProps> = ({
  location,
  regularCheckoutDestination,
}) => {
  const {
    shoppingContext: { customerOrderId },
  } = useShoppingContext();
  const {
    state: { token },
  } = useAuthentication();
  const { trackEvent } = useAnalytics();
  const { openModal } = useCommonModal();
  const [loading, setLoading] = useState(false);

  const handleInstantCheckout = useCallback(async () => {
    trackEvent({
      event: 'instant_checkout_click',
      instant_checkout: {
        location,
      },
    });
    setLoading(true);
    const response = await orderOrchestrationServiceClient.placeOrder({
      authHeader: token!.authHeader,
      customerOrderId,
      orderOptions: { instantCheckout: true },
    });

    if (!response.error) {
      // TODO: Do citrusAd and GA reporting here
      window.location.href = pathJoin(urls.orderConfirmation, customerOrderId);
    } else {
      openModal(getInstantCheckoutErrorModal(response.error, location));
    }
  }, [customerOrderId, location, openModal, token, trackEvent]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.content} aria-hidden={loading}>
        Check out in seconds with Instant Checkout - or check out as normal to shop your missed
        favourites, add vouchers and more
      </div>
      <div className={styles.buttons} aria-hidden={loading}>
        <Button
          label="Check out as normal"
          theme="secondary"
          disabled={loading}
          onClick={() => {
            trackEvent({
              event: 'review_checkout_click',
              instant_checkout: {
                location,
              },
            });
            window.location.href = regularCheckoutDestination;
          }}
        />
        <Button
          label="Instant checkout"
          theme="primary"
          disabled={loading}
          onClick={handleInstantCheckout}
        />
      </div>
      {loading && (
        <div className={styles.spinner}>
          <Spinner isActive ariaTextActive="Loading... Waiting for instant checkout to finish" />
        </div>
      )}
    </div>
  );
};

export default InstantCheckoutModalContent;
